import React, { useState, useEffect } from 'react';
import { IAgendaConference } from '../../event/eventInterface';
import Conference from '../../conference/conference';
import { FC } from 'react';
import { IUserDetail } from '../../context/userContext';
import { Button, Snackbar } from '@getgo/chameleon-web/react';
import { ON_JOIN_SESSION, ON_JOIN_SESSION_CLICK } from '../../../mixpanel/mixpanelConstants';
import {
  onJoinSessionStatusTracker,
  onJoinSessionTracker,
} from '../../../mixpanel/Feature/joinSessionTracker';
import API from '../../utils/api';
import { conferenceStatus } from '../../utils/eventDataFormat';
import { ConferenceStatusUtils } from '../../utils/conferenceStatusUtils';

export interface AgendaDetailProps {
  conferenceList: IAgendaConference[];
  handleEventSpeakerChange: (index: number, speakerId: string) => void;
  userDetail: IUserDetail;
  eventKey?: string;
  isMobOrTabView: boolean;
}

export interface IConferenceSnackbarContext {
  [key: string]: boolean;
}

export interface IConferenceSnackbarActions {
  [key: string]: ReturnType<typeof setTimeout>;
}

const AgendaDetail: FC<AgendaDetailProps> = ({
  conferenceList = [],
  handleEventSpeakerChange,
  userDetail,
  eventKey,
  isMobOrTabView,
}) => {
  const [snackbarStatus, setSnackbarStatus] = useState<IAgendaConference[]>([]);
  const [hasJoinedSession, setJoinedSession] = useState<IConferenceSnackbarContext>({});
  const snackbarStatusActions: IConferenceSnackbarActions = {};
  useEffect(() => {
    const updatedSnackbarStatus = [...snackbarStatus];
    conferenceList?.forEach((conference: IAgendaConference) => {
      const { start, end, conferenceKey } = conference;
      const status = conferenceStatus(new Date(start), new Date(end));
      if (status.toLowerCase() === ConferenceStatusUtils.status.ongoing && conferenceKey) {
        updatedSnackbarStatus.push(conference);
        setSnackbarTimeout(conference.conferenceKey);
      }
    });
    setSnackbarStatus(updatedSnackbarStatus);
    return () => {
      Object.keys(snackbarStatusActions).forEach((conferenceKey) => {
        clearTimeout(snackbarStatusActions[conferenceKey]); // cleanup
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSnackbarStatusDetails = (conference: IAgendaConference) => {
    setSnackbarTimeout(conference.conferenceKey);
    setSnackbarStatus((snackbarStatus) => [...snackbarStatus, conference]);
  };

  const setSnackbarTimeout = (conferenceKey: string) => {
    if (!snackbarStatusActions[conferenceKey]) {
      snackbarStatusActions[conferenceKey] = setTimeout(() => {
        hideSnackbar(conferenceKey);
      }, 1000 * 60 * 1);
    }
  };

  const joinWebinar = async (
    conferenceKey: string,
    actionType: 'JOIN_LINK' | 'VIEW_RECORDING',
    eventName: string,
    eventNameStatus: string,
  ) => {
    const requestData = {
      eventRegistrationId: userDetail?.eventRegistrationId,
      conferenceKey: conferenceKey,
    };
    onJoinSessionTracker(
      {
        userDetail,
        conferenceKey,
        eventKey,
      },
      eventName,
    );
    const joinWindowReference: any = window.open();
    try {
      const response = await API.getConferenceRegistration(requestData);
      if (response.status === 200) {
        let joinOrViewRecordingLink = '';
        let linkDesc = {};
        if (actionType === 'JOIN_LINK') {
          setJoinedSession({ ...hasJoinedSession, [conferenceKey]: true });
          hideSnackbar(conferenceKey);
          joinOrViewRecordingLink = response.data.url;
          linkDesc = { joinLink: joinOrViewRecordingLink, description: 'Joining Webinar Link' };
        } else {
          joinOrViewRecordingLink = `${process.env.REACT_APP_ATTENDEE_SPA_BASE_URL}recordingView?webinarKey=${conferenceKey}&registrantEmail=${userDetail?.email}`;
          linkDesc = {
            viewRecordingLink: joinOrViewRecordingLink,
            description: 'Joining Recording Link Of Webinar',
          };
        }
        onJoinSessionStatusTracker(
          {
            userDetail,
            conferenceKey,
            actionType,
            ...linkDesc,
          },
          eventNameStatus,
        );
        if (joinWindowReference) {
          joinWindowReference.location = joinOrViewRecordingLink;
        }
      } else {
        if (joinWindowReference) {
          joinWindowReference.close();
        }
      }
    } catch (e) {
      if (joinWindowReference) {
        joinWindowReference.close();
      }
    }
  };

  const hideSnackbar = (conferenceKey: string) => {
    setSnackbarStatus((snackbarStatus) => [
      ...snackbarStatus.filter((conference) => conference.conferenceKey !== conferenceKey),
    ]);
    clearTimeout(snackbarStatusActions[conferenceKey]);
    delete snackbarStatusActions[conferenceKey];
  };

  const renderSnackBar = () => {
    return snackbarStatus.map((conference) => (
      <div key={conference.conferenceKey} className="snackbar">
        <Snackbar
          title=""
          stacked={isMobOrTabView}
          actions={
            <>
              <Button
                size="small"
                variant="neutral-inverse"
                onClick={() =>
                  joinWebinar(
                    conference.conferenceKey,
                    'JOIN_LINK',
                    ON_JOIN_SESSION_CLICK,
                    ON_JOIN_SESSION,
                  )
                }
              >
                Join
              </Button>
              <Button
                size="small"
                variant="neutral-inverse"
                onClick={() => hideSnackbar(conference.conferenceKey)}
              >
                Not now
              </Button>
            </>
          }
        >
          {conference.title} has started
        </Snackbar>
      </div>
    ));
  };
  return (
    <div className="agenda-detail">
      {conferenceList?.map((conference: IAgendaConference) => (
        <Conference
          conference={conference}
          key={conference.title}
          handleEventSpeakerChange={handleEventSpeakerChange}
          userDetail={userDetail}
          eventKey={eventKey}
          hasJoinedSession={hasJoinedSession[conference.conferenceKey]}
          joinWebinar={joinWebinar}
          setSnackbarStatusDetails={setSnackbarStatusDetails}
        />
      ))}
      {userDetail && <div className="snackbar-container">{renderSnackBar()}</div>}
    </div>
  );
};

export default AgendaDetail;
